// PHASE_0 B0 deliverable 4 — the display-only money formatter.
//
// The CLAUDE.md money invariant: storage + API edge carry money as
// `<name>_minor_units` (integer) + `currency` (ISO-4217). <Money> is the
// ONLY place those reassemble into a locale-formatted decimal string — no
// feature formats money inline, and the server never receives a formatted
// string or a float. Formatting delegates to @novemai/i18n-sdk's
// `formatMoney`, which derives minor-unit digits from Intl currency data
// (zero-decimal currencies like JPY handled correctly).
//
// Locale is a prop (defaults to the SDK source locale) so the component
// stays free of any i18n runtime dependency — the consuming app passes its
// resolved locale (e.g. `i18n.language` from react-i18next).

import { forwardRef, type HTMLAttributes } from "react";

import { DEFAULT_LOCALE, formatMoney, type LocaleCode } from "@novemai/i18n-sdk";

import { cx } from "../lib/cx.ts";

export interface MoneyProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /** Integer minor units (100 kuruş = 1 TL). Floats are a contract violation. */
  readonly minorUnits: number;
  /** ISO-4217 currency code paired with the amount on the same row. */
  readonly currency: string;
  /** Display locale — defaults to the SDK source locale. */
  readonly locale?: LocaleCode;
}

/**
 * Render an integer minor-unit amount as a locale-formatted currency string.
 * The amount is wrapped in a `<span data-component="money">` carrying the
 * raw minor units + currency as data-attrs so tests/automation can read the
 * unformatted value without re-parsing the localized string.
 */
export const Money = forwardRef<HTMLSpanElement, MoneyProps>(
  ({ minorUnits, currency, locale = DEFAULT_LOCALE, className, ...rest }, ref) => {
    const formatted = formatMoney(minorUnits, currency, locale);
    return (
      <span
        ref={ref}
        data-component="money"
        data-minor-units={minorUnits}
        data-currency={currency}
        className={cx("nai-money", className)}
        {...rest}
      >
        {formatted}
      </span>
    );
  },
);
Money.displayName = "Money";
